import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient, API_BASE } from '../api/client';

// ── Type Definitions ──────────────────────────────────────────

interface CatalogItem {
  id: string;
  name: string;
  description: string | null;
  price: number | null;
  imageUrl: string | null;
  category: string | null;
  tags: string | null;
  featured: boolean;
}

interface VendorRegistration {
  boothNumber: string | null;
  zone: string | null;
  description: string | null;
  status: string;
  event: { id: string; name: string };
}

interface ShowcaseVendor {
  id: string;
  businessName: string;
  category: string | null;
  contactPerson: string;
  phone: string | null;
  catalogItems: CatalogItem[];
  eventRegistrations: VendorRegistration[];
}

// ── Helpers ───────────────────────────────────────────────────

function resolveImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
}

function formatPrice(price: number | null): string {
  if (price === null || price === undefined) return '';
  const num = Number(price);
  if (num === 0) return '';
  return `$${num.toFixed(2)}`;
}

const CATEGORY_ICONS: Record<string, string> = {
  Food: '🍲',
  Merchandise: '🛍️',
  Services: '✂️',
  Entertainment: '🎵',
  'Health & Wellness': '💆',
  'Arts & Crafts': '🎨',
  Other: '📦',
};

// ── Inline Styles ─────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f0f7f2 0%, #e8f5e9 50%, #f5f0e8 100%)',
    paddingBottom: '2rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    background: 'linear-gradient(135deg, #0f2d1f 0%, #1a6b3c 100%)',
    padding: '1.5rem 1rem 1rem',
    borderRadius: '0 0 24px 24px',
    marginBottom: '1rem',
    position: 'relative' as const,
    overflow: 'hidden' as const,
  },
  headerPattern: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.05,
    backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 20px, #fff 20px, #fff 21px)',
  },
  headerTitle: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#fff',
    margin: '0 0 0.25rem 0',
    position: 'relative' as const,
    zIndex: 1,
  },
  headerSubtitle: {
    fontSize: '0.85rem',
    color: '#a3d4b8',
    margin: 0,
    position: 'relative' as const,
    zIndex: 1,
  },
  searchBar: {
    margin: '0 1rem',
    position: 'relative' as const,
    marginTop: '-1.25rem',
    zIndex: 2,
  },
  searchInput: {
    width: '100%',
    padding: '0.75rem 1rem 0.75rem 2.5rem',
    border: 'none',
    borderRadius: '16px',
    fontSize: '0.9rem',
    backgroundColor: '#fff',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  searchIcon: {
    position: 'absolute' as const,
    left: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '1rem',
    opacity: 0.5,
  },
  filterScroll: {
    display: 'flex',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    overflowX: 'auto' as const,
    WebkitOverflowScrolling: 'touch' as const,
    scrollbarWidth: 'none' as const,
    msOverflowStyle: 'none' as const,
  },
  filterChip: {
    flexShrink: 0,
    padding: '0.4rem 0.9rem',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 500,
    border: '1.5px solid #e0e0e0',
    backgroundColor: '#fff',
    color: '#555',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap' as const,
  },
  filterChipActive: {
    flexShrink: 0,
    padding: '0.4rem 0.9rem',
    borderRadius: '20px',
    fontSize: '0.8rem',
    fontWeight: 600,
    border: '1.5px solid #1a6b3c',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    whiteSpace: 'nowrap' as const,
  },
  featuredSection: {
    padding: '0 1rem',
    marginBottom: '1rem',
  },
  sectionLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#1a6b3c',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  featuredScroll: {
    display: 'flex',
    gap: '0.75rem',
    overflowX: 'auto' as const,
    WebkitOverflowScrolling: 'touch' as const,
    scrollbarWidth: 'none' as const,
    paddingBottom: '0.5rem',
  },
  featuredCard: {
    flexShrink: 0,
    width: '160px',
    borderRadius: '16px',
    overflow: 'hidden' as const,
    backgroundColor: '#fff',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    transition: 'transform 0.2s ease',
  },
  featuredImg: {
    width: '160px',
    height: '120px',
    objectFit: 'contain' as const,
    backgroundColor: '#f9fafb',
  },
  featuredImgPlaceholder: {
    width: '160px',
    height: '120px',
    backgroundColor: '#e8f5e9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2rem',
  },
  featuredInfo: {
    padding: '0.5rem 0.6rem',
  },
  featuredName: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#0f2d1f',
    margin: '0 0 0.15rem 0',
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const,
  },
  featuredVendor: {
    fontSize: '0.7rem',
    color: '#6b7280',
    margin: 0,
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const,
  },
  featuredPrice: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#c9a84c',
    marginTop: '0.2rem',
  },
  vendorList: {
    padding: '0 1rem',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '1rem',
  },
  vendorCard: {
    backgroundColor: '#fff',
    borderRadius: '20px',
    marginBottom: '1rem',
    boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
    overflow: 'hidden' as const,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  vendorHeader: {
    padding: '1rem 1rem 0.75rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    cursor: 'pointer',
  },
  vendorInfo: {
    flex: 1,
    minWidth: 0,
  },
  vendorName: {
    fontSize: '1.05rem',
    fontWeight: 700,
    color: '#0f2d1f',
    margin: '0 0 0.2rem 0',
  },
  vendorCategory: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.75rem',
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    padding: '0.15rem 0.5rem',
    borderRadius: '10px',
  },
  vendorBooth: {
    fontSize: '0.7rem',
    color: '#1a6b3c',
    fontWeight: 500,
    marginTop: '0.3rem',
  },
  vendorBadge: {
    flexShrink: 0,
    width: '42px',
    height: '42px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #1a6b3c, #2e8b57)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.3rem',
  },
  itemCount: {
    fontSize: '0.7rem',
    color: '#6b7280',
    marginTop: '0.25rem',
  },
  callingCard: {
    padding: '1rem 1.25rem 1.25rem',
    textAlign: 'center' as const,
    borderTop: '1px dashed #e5e7eb',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '0.3rem',
  },
  callingCardText: {
    fontSize: '0.9rem',
    color: '#374151',
    lineHeight: 1.5,
    margin: 0,
  },
  callingCardContact: {
    fontSize: '0.82rem',
    color: '#166534',
    textDecoration: 'none',
    fontWeight: 600,
  },
  callingCardNote: {
    fontSize: '0.72rem',
    color: '#9ca3af',
    fontStyle: 'italic' as const,
    marginTop: '0.3rem',
  },
  catalogGrid: {
    display: 'flex',
    gap: '0.75rem',
    padding: '0 1rem 1rem',
    overflowX: 'auto' as const,
    WebkitOverflowScrolling: 'touch' as const,
    scrollbarWidth: 'none' as const,
    scrollSnapType: 'x mandatory' as const,
    msOverflowStyle: 'none' as const,
  },
  catalogItem: {
    flexShrink: 0,
    width: '150px',
    borderRadius: '14px',
    overflow: 'hidden' as const,
    backgroundColor: '#fafafa',
    border: '1px solid #f0f0f0',
    transition: 'transform 0.15s ease',
    scrollSnapAlign: 'start' as const,
  },
  itemImage: {
    width: '100%',
    height: '130px',
    objectFit: 'contain' as const,
    backgroundColor: '#f9fafb',
    display: 'block',
  },
  itemImagePlaceholder: {
    width: '100%',
    height: '130px',
    backgroundColor: '#e8f5e9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
  },
  itemInfo: {
    padding: '0.5rem 0.6rem',
  },
  itemName: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#0f2d1f',
    margin: '0 0 0.15rem 0',
    overflow: 'hidden' as const,
    textOverflow: 'ellipsis' as const,
    whiteSpace: 'nowrap' as const,
  },
  itemDesc: {
    fontSize: '0.7rem',
    color: '#6b7280',
    margin: 0,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden' as const,
    lineHeight: '1.3',
  },
  itemPrice: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#c9a84c',
    marginTop: '0.3rem',
  },
  itemTags: {
    display: 'flex',
    gap: '0.25rem',
    flexWrap: 'wrap' as const,
    marginTop: '0.3rem',
  },
  tag: {
    fontSize: '0.6rem',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    padding: '0.1rem 0.4rem',
    borderRadius: '6px',
  },
  expandToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.5rem',
    fontSize: '0.75rem',
    color: '#1a6b3c',
    fontWeight: 600,
    cursor: 'pointer',
    borderTop: '1px solid #f0f0f0',
    gap: '0.3rem',
  },
  empty: {
    textAlign: 'center' as const,
    padding: '3rem 1rem',
    color: '#6b7280',
    fontSize: '0.9rem',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '0.75rem',
    display: 'block',
  },
  loadingSpinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '3rem',
    fontSize: '1.2rem',
    color: '#1a6b3c',
  },
  // Detail modal
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem',
  },
  modalSheet: {
    backgroundColor: '#fff',
    borderRadius: '20px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '85vh',
    overflowY: 'auto' as const,
    position: 'relative' as const,
    animation: 'slideUp 0.3s ease',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  modalHandle: {
    width: '40px',
    height: '4px',
    backgroundColor: '#ddd',
    borderRadius: '2px',
    margin: '0.75rem auto 0',
  },
  modalImage: {
    width: '100%',
    maxHeight: '300px',
    objectFit: 'contain' as const,
    backgroundColor: '#f5f5f5',
    display: 'block',
  },
  modalImagePlaceholder: {
    width: '100%',
    height: '200px',
    background: 'linear-gradient(135deg, #e8f5e9, #c8e6c9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '4rem',
  },
  modalBody: {
    padding: '1.25rem',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: '#0f2d1f',
    margin: '0 0 0.3rem 0',
  },
  modalVendor: {
    fontSize: '0.85rem',
    color: '#6b7280',
    marginBottom: '0.75rem',
  },
  modalPrice: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: '#c9a84c',
    marginBottom: '0.75rem',
  },
  modalDesc: {
    fontSize: '0.9rem',
    color: '#333',
    lineHeight: '1.6',
    marginBottom: '1rem',
  },
  modalTags: {
    display: 'flex',
    gap: '0.4rem',
    flexWrap: 'wrap' as const,
    marginBottom: '1rem',
  },
  modalTag: {
    fontSize: '0.75rem',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    padding: '0.25rem 0.6rem',
    borderRadius: '8px',
    fontWeight: 500,
  },
  modalVendorInfo: {
    backgroundColor: '#f9fafb',
    borderRadius: '12px',
    padding: '0.75rem 1rem',
    marginBottom: '1rem',
  },
  modalVendorLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.3rem',
  },
  modalVendorName: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#0f2d1f',
  },
  modalBooth: {
    fontSize: '0.8rem',
    color: '#1a6b3c',
    marginTop: '0.25rem',
  },
  modalClose: {
    position: 'absolute' as const,
    top: '0.75rem',
    right: '0.75rem',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: 'rgba(0,0,0,0.4)',
    color: '#fff',
    border: 'none',
    fontSize: '1.1rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  statsBar: {
    display: 'flex',
    gap: '0.75rem',
    padding: '0 1rem 0.75rem',
    overflowX: 'auto' as const,
  },
  statPill: {
    flexShrink: 0,
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '0.5rem 0.75rem',
    boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
    textAlign: 'center' as const,
    minWidth: '70px',
  },
  statNumber: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#1a6b3c',
  },
  statLabel: {
    fontSize: '0.65rem',
    color: '#6b7280',
    marginTop: '0.1rem',
  },
};

// ── Component ─────────────────────────────────────────────────

export function VendorShowcasePage() {

  const [vendors, setVendors] = useState<ShowcaseVendor[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [expandedVendors, setExpandedVendors] = useState<Set<string>>(new Set());

  // Detail modal
  const [selectedItem, setSelectedItem] = useState<{ item: CatalogItem; vendor: ShowcaseVendor } | null>(null);

  // Touch handling for swipe-to-dismiss modal
  const modalRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef(0);

  const fetchShowcase = useCallback(async () => {
    try {
      setError('');
      const params = new URLSearchParams();
      if (activeCategory) params.set('category', activeCategory);
      const query = params.toString() ? `?${params.toString()}` : '';
      const data = await apiClient<ShowcaseVendor[]>(`/vendor-catalog/showcase${query}`);
      setVendors(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load vendor showcase');
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await apiClient<string[]>('/vendor-catalog/categories');
      setCategories(data);
    } catch {
      // Non-critical
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { setLoading(true); fetchShowcase(); }, [fetchShowcase]);

  // ── Filtering logic ──

  const filteredVendors = vendors.filter((v) => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    const nameMatch = v.businessName.toLowerCase().includes(q);
    const itemMatch = v.catalogItems.some(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        (item.tags && item.tags.toLowerCase().includes(q)) ||
        (item.description && item.description.toLowerCase().includes(q))
    );
    return nameMatch || itemMatch;
  });

  // Collect all featured items across vendors
  const featuredItems = vendors
    .flatMap((v) => v.catalogItems.filter((i) => i.featured).map((item) => ({ item, vendor: v })))
    .slice(0, 10);

  // Stats
  const totalItems = vendors.reduce((sum, v) => sum + v.catalogItems.length, 0);
  const totalVendors = vendors.length;

  // ── Handlers ──

  const toggleExpand = (vendorId: string) => {
    setExpandedVendors((prev) => {
      const next = new Set(prev);
      if (next.has(vendorId)) next.delete(vendorId);
      else next.add(vendorId);
      return next;
    });
  };

  const openDetail = (item: CatalogItem, vendor: ShowcaseVendor) => {
    setSelectedItem({ item, vendor });
  };

  const closeDetail = () => setSelectedItem(null);

  const handleModalTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleModalTouchEnd = (e: React.TouchEvent) => {
    const diff = e.changedTouches[0].clientY - touchStartY.current;
    if (diff > 100) closeDetail(); // swipe down to dismiss
  };

  // ── Render helpers ──

  const renderFeaturedSection = () => {
    if (featuredItems.length === 0) return null;
    return (
      <div style={s.featuredSection}>
        <div style={s.sectionLabel}>
          <span>⭐</span> Featured Items
        </div>
        <div style={s.featuredScroll}>
          {featuredItems.map(({ item, vendor }) => (
            <div
              key={item.id}
              style={s.featuredCard}
              onClick={() => openDetail(item, vendor)}
              role="button"
              tabIndex={0}
              aria-label={`View ${item.name} from ${vendor.businessName}`}
              onKeyDown={(e) => e.key === 'Enter' && openDetail(item, vendor)}
            >
              {item.imageUrl ? (
                <img
                  src={resolveImageUrl(item.imageUrl)!}
                  alt={item.name}
                  style={s.featuredImg}
                  loading="lazy"
                />
              ) : (
                <div style={s.featuredImgPlaceholder}>
                  {CATEGORY_ICONS[item.category || ''] || '📦'}
                </div>
              )}
              <div style={s.featuredInfo}>
                <p style={s.featuredName}>{item.name}</p>
                <p style={s.featuredVendor}>{vendor.businessName}</p>
                {item.price !== null && formatPrice(item.price) && (
                  <div style={s.featuredPrice}>{formatPrice(item.price)}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderVendorCard = (vendor: ShowcaseVendor) => {
    const isExpanded = expandedVendors.has(vendor.id);
    const displayItems = isExpanded ? vendor.catalogItems : vendor.catalogItems.slice(0, 4);
    const hasMore = vendor.catalogItems.length > 4;
    const boothInfo = vendor.eventRegistrations.find((r) => r.boothNumber);
    const icon = CATEGORY_ICONS[vendor.category || ''] || '🏪';
    const hasItems = vendor.catalogItems.length > 0;

    // Vendors without any catalog items get a generic "calling card" instead of
    // a product grid — so every registered vendor is listed.
    if (!hasItems) {
      return (
        <div key={vendor.id} style={s.vendorCard}>
          <div style={{ ...s.vendorHeader, cursor: 'default' }}>
            <div style={s.vendorInfo}>
              <h3 style={s.vendorName}>{vendor.businessName}</h3>
              {vendor.category && (
                <span style={s.vendorCategory}>{icon} {vendor.category}</span>
              )}
              {boothInfo && (
                <div style={s.vendorBooth}>
                  📍 Booth {boothInfo.boothNumber}{boothInfo.zone && ` · ${boothInfo.zone}`}
                </div>
              )}
            </div>
            <div style={s.vendorBadge}>{icon}</div>
          </div>
          <div style={s.callingCard}>
            <div style={{ fontSize: '1.6rem', marginBottom: '0.4rem' }}>{icon}</div>
            <p style={s.callingCardText}>
              Visit <strong>{vendor.businessName}</strong>{boothInfo ? ` at Booth ${boothInfo.boothNumber}` : ' at the event'} this weekend.
            </p>
            {vendor.contactPerson && (
              <div style={s.callingCardContact}>👤 {vendor.contactPerson}</div>
            )}
            {vendor.phone && (
              <a href={`tel:${vendor.phone}`} style={s.callingCardContact}>📞 {vendor.phone}</a>
            )}
            <div style={s.callingCardNote}>Full offerings coming soon</div>
          </div>
        </div>
      );
    }

    return (
      <div key={vendor.id} style={s.vendorCard}>
        {/* Vendor header — tap to expand/collapse */}
        <div
          style={s.vendorHeader}
          onClick={() => toggleExpand(vendor.id)}
          role="button"
          tabIndex={0}
          aria-expanded={isExpanded}
          aria-label={`${vendor.businessName} - ${vendor.catalogItems.length} items`}
          onKeyDown={(e) => e.key === 'Enter' && toggleExpand(vendor.id)}
        >
          <div style={s.vendorInfo}>
            <h3 style={s.vendorName}>{vendor.businessName}</h3>
            {vendor.category && (
              <span style={s.vendorCategory}>
                {icon} {vendor.category}
              </span>
            )}
            {boothInfo && (
              <div style={s.vendorBooth}>
                📍 Booth {boothInfo.boothNumber}
                {boothInfo.zone && ` · ${boothInfo.zone}`}
              </div>
            )}
            <div style={s.itemCount}>
              {vendor.catalogItems.length} item{vendor.catalogItems.length !== 1 ? 's' : ''} available
            </div>
          </div>
          <div style={s.vendorBadge}>{icon}</div>
        </div>

        {/* Catalog grid */}
        <div style={s.catalogGrid}>
          {displayItems.map((item) => (
            <div
              key={item.id}
              style={s.catalogItem}
              onClick={() => openDetail(item, vendor)}
              role="button"
              tabIndex={0}
              aria-label={`${item.name}${item.price ? ` - ${formatPrice(item.price)}` : ''}`}
              onKeyDown={(e) => e.key === 'Enter' && openDetail(item, vendor)}
            >
              {item.imageUrl ? (
                <img
                  src={resolveImageUrl(item.imageUrl)!}
                  alt={item.name}
                  style={s.itemImage}
                  loading="lazy"
                />
              ) : (
                <div style={s.itemImagePlaceholder}>
                  {CATEGORY_ICONS[item.category || ''] || '📦'}
                </div>
              )}
              <div style={s.itemInfo}>
                <p style={s.itemName}>{item.name}</p>
                {item.description && <p style={s.itemDesc}>{item.description}</p>}
                {item.price !== null && formatPrice(item.price) && (
                  <div style={s.itemPrice}>{formatPrice(item.price)}</div>
                )}
                {item.tags && (
                  <div style={s.itemTags}>
                    {item.tags.split(',').slice(0, 3).map((tag, i) => (
                      <span key={i} style={s.tag}>{tag.trim()}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Expand/collapse toggle */}
        {hasMore && (
          <div
            style={s.expandToggle}
            onClick={() => toggleExpand(vendor.id)}
            role="button"
            tabIndex={0}
            aria-label={isExpanded ? 'Show less' : `Show all ${vendor.catalogItems.length} items`}
            onKeyDown={(e) => e.key === 'Enter' && toggleExpand(vendor.id)}
          >
            {isExpanded ? '▲ Show less' : `▼ View all ${vendor.catalogItems.length} items`}
          </div>
        )}
      </div>
    );
  };

  const renderDetailModal = () => {
    if (!selectedItem) return null;
    const { item, vendor } = selectedItem;
    const boothInfo = vendor.eventRegistrations.find((r) => r.boothNumber);

    return (
      <div
        style={s.modalOverlay}
        onClick={(e) => e.target === e.currentTarget && closeDetail()}
        role="dialog"
        aria-modal="true"
        aria-label={`${item.name} details`}
      >
        <div
          ref={modalRef}
          style={s.modalSheet}
          onTouchStart={handleModalTouchStart}
          onTouchEnd={handleModalTouchEnd}
        >
          <div style={s.modalHandle} />
          <button
            style={s.modalClose}
            onClick={closeDetail}
            aria-label="Close detail view"
          >
            ✕
          </button>

          {item.imageUrl ? (
            <img
              src={resolveImageUrl(item.imageUrl)!}
              alt={item.name}
              style={s.modalImage}
            />
          ) : (
            <div style={s.modalImagePlaceholder}>
              {CATEGORY_ICONS[item.category || ''] || '📦'}
            </div>
          )}

          <div style={s.modalBody}>
            <h2 style={s.modalTitle}>{item.name}</h2>
            <p style={s.modalVendor}>by {vendor.businessName}</p>

            {item.price !== null && formatPrice(item.price) && (
              <div style={s.modalPrice}>{formatPrice(item.price)}</div>
            )}

            {item.description && (
              <p style={s.modalDesc}>{item.description}</p>
            )}

            {item.tags && (
              <div style={s.modalTags}>
                {item.tags.split(',').map((tag, i) => (
                  <span key={i} style={s.modalTag}>{tag.trim()}</span>
                ))}
              </div>
            )}

            <div style={s.modalVendorInfo}>
              <div style={s.modalVendorLabel}>Vendor</div>
              <div style={s.modalVendorName}>
                {CATEGORY_ICONS[vendor.category || ''] || '🏪'} {vendor.businessName}
              </div>
              {boothInfo && (
                <div style={s.modalBooth}>
                  📍 Booth {boothInfo.boothNumber}
                  {boothInfo.zone && ` · ${boothInfo.zone} Zone`}
                  {boothInfo.event && ` · ${boothInfo.event.name}`}
                </div>
              )}
              {vendor.phone && (
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
                  📞 {vendor.phone}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Main render ──

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.header}>
          <div style={s.headerPattern} />
          <h1 style={s.headerTitle}>🏪 Vendor Showcase</h1>
          <p style={s.headerSubtitle}>Discover what's available this weekend</p>
        </div>
        <div style={s.loadingSpinner}>Loading vendors...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={s.page}>
        <div style={s.header}>
          <div style={s.headerPattern} />
          <h1 style={s.headerTitle}>🏪 Vendor Showcase</h1>
          <p style={s.headerSubtitle}>Discover what's available this weekend</p>
        </div>
        <div style={{ ...s.empty, color: '#d32f2f' }}>
          <span style={s.emptyIcon}>⚠️</span>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div style={s.headerPattern} />
        <h1 style={s.headerTitle}>🏪 Vendor Showcase</h1>
        <p style={s.headerSubtitle}>Browse items from our reunion weekend vendors</p>
      </div>

      {/* Search bar */}
      <div style={s.searchBar}>
        <span style={s.searchIcon}>🔍</span>
        <input
          type="search"
          placeholder="Search vendors or items..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={s.searchInput}
          aria-label="Search vendors or items"
        />
      </div>

      {/* Stats pills */}
      <div style={s.statsBar}>
        <div style={s.statPill}>
          <div style={s.statNumber}>{totalVendors}</div>
          <div style={s.statLabel}>Vendors</div>
        </div>
        <div style={s.statPill}>
          <div style={s.statNumber}>{totalItems}</div>
          <div style={s.statLabel}>Items</div>
        </div>
        <div style={s.statPill}>
          <div style={s.statNumber}>{featuredItems.length}</div>
          <div style={s.statLabel}>Featured</div>
        </div>
        <div style={s.statPill}>
          <div style={s.statNumber}>{categories.length}</div>
          <div style={s.statLabel}>Categories</div>
        </div>
      </div>

      {/* Category filter chips */}
      <div style={s.filterScroll}>
        <button
          style={activeCategory === '' ? s.filterChipActive : s.filterChip}
          onClick={() => setActiveCategory('')}
          aria-pressed={activeCategory === ''}
        >
          All
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            style={activeCategory === cat ? s.filterChipActive : s.filterChip}
            onClick={() => setActiveCategory(cat)}
            aria-pressed={activeCategory === cat}
          >
            {CATEGORY_ICONS[cat] || '📦'} {cat}
          </button>
        ))}
      </div>

      {/* Featured horizontal scroll */}
      {renderFeaturedSection()}

      {/* Vendor cards */}
      <div style={s.vendorList}>
        {filteredVendors.length === 0 ? (
          <div style={s.empty}>
            <span style={s.emptyIcon}>🔍</span>
            {searchText
              ? `No results for "${searchText}"`
              : 'No vendors have catalog items yet. Check back soon!'}
          </div>
        ) : (
          filteredVendors.map(renderVendorCard)
        )}
      </div>

      {/* Item detail bottom sheet modal */}
      {renderDetailModal()}

      {/* Inline animation keyframes */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
